import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { DateRangePicker, Icon, Styles } from "@/shared/coralreef";
import { percent, rem } from "csx";
import { Content, Horizontal, Stretch } from "gls/lib";
import moment from "moment";
import React, { FC, useEffect, useState } from "react";
import type { Cage, Site } from "@/shared/context/sites.graphql";
import { style, stylesheet } from "typestyle";
import { useShellHost } from "@/shell-handle/ShellHostProvider";
import { SiteCagePicker } from "./siteCagePicker/SiteCagePicker";

export type MappedToolbarSite = {
  activeSites: MappedSite[];
  fallowedSites: FallowedSiteWithYear[];
};

export type FallowedSiteWithYear = MappedSite & { year: string };

export type MappedSite = Omit<Site, "cageGroups"> & {
  cageGroups: MappedCageGroup[];
};

export type MappedCageGroup = {
  id: number;
  name: string;
  cages: Cage[];
};

export const Toolbar: FCWithChildren<{
  icon: IconDefinition;
  title: string;
  withDates?: boolean;
  hideSitePicker?: boolean;
  isSingleSite?: boolean;
}> = React.memo((props) => {
  const { children, icon, title, hideSitePicker, withDates } = props;
  const host = useShellHost();
  const [range, setRangeState] = useState(host.handle.dateRange.range.get());
  useEffect(
    () =>
      host.handle.dateRange.range.subscribe(() =>
        setRangeState(host.handle.dateRange.range.get()),
      ),
    [host],
  );
  const start = range.start ? moment(range.start) : null;
  const end = range.end ? moment(range.end) : null;

  return (
    <Horizontal
      height={TOOLBAR_HEIGHT}
      className={styles.toolbar}
      verticalAlign="center"
      padding={[0, rem(2)]}
      spacing={rem(2)}
      data-testid="toolbar"
      width={percent(100)}
    >
      <ToolbarBreadCrumb icon={icon} title={title} />

      <Stretch horizontalAlign="center">{children}</Stretch>
      <Horizontal verticalAlign="center">
        {withDates && (
          <DateRangePicker
            withConfirmButton
            endDate={end}
            startDate={start}
            maxDate={moment().endOf("D")}
            onChange={({ startDate, endDate }) => {
              host.handle.dateRange.setRange(
                startDate ? startDate.startOf("day").toISOString() : null,
                endDate ? endDate.endOf("day").toISOString() : null,
              );
            }}
          />
        )}
        {!hideSitePicker && <SiteCagePicker />}
      </Horizontal>
    </Horizontal>
  );
});

const ToolbarBreadCrumb: FC<{
  icon: IconDefinition;
  title: string;
}> = (props) => {
  const { icon, title } = props;
  return title ? (
    <Horizontal
      height={percent(100)}
      spacing={rem(0.5)}
      verticalAlign="center"
      className={style({ color: Styles.Colours.textLight })}
      data-testid="toolbar-breadcrumb"
    >
      <Icon icon={icon} />
      <Content
        verticalAlign="bottom"
        height={rem(1.6)}
        data-testid="toolbar-breadcrumb-text"
      >
        {title}
      </Content>
    </Horizontal>
  ) : null;
};

export const TOOLBAR_HEIGHT = rem(3);

const styles = stylesheet({
  toolbar: {
    background: Styles.Colours.darkerBlue,
    boxShadow: `0 4px 6px rgb(0 0 0 / 15%)`,
    zIndex: 998,
    position: "relative",
    boxSizing: "border-box",
  },
});
